import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { Play, Pause, ArrowLeft, Volume2, VolumeX, Maximize, ChevronsLeft, ChevronsRight, StickyNote, Gauge, Map, Radio, User, Compass, Car } from "lucide-react";
import { Button } from "../components/ui/button";

function fmt(sec) {
  sec = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const CHAP_KIND_COLOR = {
  observacion: "bg-cyan-400", maniobra: "bg-orange-500",
  error: "bg-red-500", info: "bg-white/60",
};

function resolveVideoUrl(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

function DemoTag() {
  return (
    <span className="absolute top-2 right-2 z-10 text-[8px] uppercase tracking-widest text-white/40 bg-white/5 px-1.5 py-0.5 rounded-full backdrop-blur-md border border-white/10" title="Panel decorativo sin telemetría real">
      Demo
    </span>
  );
}

/* ---------- Decoraciones fallback (cuando no hay vídeo real para esa pista) ---------- */
function DashboardDeco({ progress }) {
  const speed = Math.max(0, Math.round(30 + 30 * Math.sin(progress * 0.6) + 20 * Math.sin(progress * 0.15)));
  const rpm = 1000 + Math.abs(1500 * Math.sin(progress * 0.9)) + 400;
  const angle = (rpm / 8000) * 240 - 120;
  return (
    <div className="relative h-full w-full bg-gradient-to-br from-neutral-900 via-neutral-950 to-black flex items-center justify-around overflow-hidden px-4">
      <DemoTag />
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-widest text-orange-300">Velocidad</div>
        <div className="font-heading text-4xl font-black tabular-nums bg-gradient-to-b from-white to-white/40 bg-clip-text text-transparent leading-none">{speed}</div>
        <div className="text-[10px] text-white/40">km/h</div>
      </div>
      <div className="relative h-16 w-16">
        <svg viewBox="0 0 100 100" className="absolute inset-0">
          <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
          <path d="M 20 70 A 42 42 0 1 1 80 70" fill="none" stroke="rgba(249,115,22,0.6)" strokeWidth="3" strokeLinecap="round" />
          <line x1="50" y1="50" x2="50" y2="18" stroke="#facc15" strokeWidth="2.5" strokeLinecap="round"
                style={{ transformOrigin: "50px 50px", transform: `rotate(${angle}deg)`, transition: "transform 0.25s" }} />
          <circle cx="50" cy="50" r="4" fill="#facc15" />
        </svg>
      </div>
    </div>
  );
}

function GearboxDeco({ progress }) {
  const gear = Math.max(1, Math.min(5, Math.floor((progress % 60) / 12) + 1));
  return (
    <div className="relative h-full w-full bg-gradient-to-br from-neutral-900 via-black to-neutral-950 flex items-center justify-center overflow-hidden">
      <DemoTag />
      <div className="flex items-center gap-3">
        <div className="grid grid-cols-3 gap-1 w-24">
          {[[1, "R"], [3, ""], [5, ""], [2, ""], [4, ""], [null, ""]].map(([g, alt], i) => {
            const active = g === gear;
            return (
              <div key={i} className={`h-6 rounded border flex items-center justify-center font-bold text-[10px] font-heading ${active ? "bg-gradient-to-br from-orange-400 to-amber-500 text-black border-transparent" : "bg-white/5 text-white/40 border-white/10"}`}>
                {alt || g || ""}
              </div>
            );
          })}
        </div>
        <div className="text-center">
          <div className="text-[9px] text-white/40 uppercase tracking-widest">Marcha</div>
          <div className="font-heading text-3xl font-black text-white leading-none">{gear}ª</div>
        </div>
      </div>
    </div>
  );
}

function PedalsDeco({ progress }) {
  const clutch = 100 * Math.abs(Math.sin(progress * 0.4));
  const brake = Math.max(0, 100 * Math.sin(progress * 0.25));
  const gas = Math.max(0, 100 - brake - 20 * Math.sin(progress * 0.5));
  const bars = [
    { l: "Embrague", v: clutch, c: "from-cyan-400 to-cyan-600" },
    { l: "Freno", v: brake, c: "from-red-400 to-red-600" },
    { l: "Acelerador", v: gas, c: "from-emerald-400 to-emerald-600" },
  ];
  return (
    <div className="relative h-full w-full bg-gradient-to-br from-neutral-900 via-black to-neutral-950 p-3 flex overflow-hidden">
      <DemoTag />
      <div className="flex-1 grid grid-cols-3 gap-2">
        {bars.map((b) => (
          <div key={b.l} className="flex flex-col items-center gap-1">
            <div className="text-[8px] uppercase tracking-widest text-white/40">{b.l}</div>
            <div className="flex-1 w-4 bg-white/5 rounded-full overflow-hidden relative">
              <motion.div className={`absolute bottom-0 left-0 right-0 rounded-full bg-gradient-to-t ${b.c}`}
                          animate={{ height: `${b.v}%` }} transition={{ duration: 0.2 }} />
            </div>
            <div className="text-[9px] tabular-nums text-white/60">{Math.round(b.v)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InteriorDeco() {
  return (
    <div className="relative h-full w-full bg-gradient-to-br from-neutral-800 via-neutral-900 to-black flex items-center justify-center overflow-hidden">
      <DemoTag />
      <div className="text-center">
        <Car size={28} className="mx-auto text-orange-400/70" strokeWidth={1.5} />
        <p className="mt-1 text-[10px] uppercase tracking-widest text-white/50">Vista interior</p>
        <p className="text-[9px] text-white/30">Añade una URL para activar el vídeo real</p>
      </div>
    </div>
  );
}

function ProfessorDeco() {
  return (
    <div className="relative h-full w-full bg-gradient-to-br from-neutral-800 via-neutral-900 to-black flex items-center justify-center overflow-hidden">
      <DemoTag />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(249,115,22,0.15),_transparent_60%)]" />
      <div className="text-center">
        <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-xl shadow-orange-500/30">
          <User size={22} className="text-black" />
        </div>
        <p className="mt-2 text-xs font-semibold text-white">Profesor Natur</p>
      </div>
      <div className="absolute top-2 left-2 flex items-center gap-1 text-[10px] text-red-400">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
        </span>
        LIVE
      </div>
    </div>
  );
}

/* ---------- Slave video: sincronizado con el master por currentTime/playbackRate/play/pause ---------- */
function SlaveVideo({ url, master, active, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current, m = master.videoRef.current;
    if (!v || !m || !url) return;
    v.muted = true;
    v.playbackRate = master.speed || 1;
    // Alineación temporal
    if (isFinite(m.currentTime) && Math.abs(v.currentTime - m.currentTime) > 0.3) {
      try { v.currentTime = m.currentTime; } catch { /* metadatos no listos */ }
    }
    // Reproducir solo cuando este slave está activo (main view) o es fijo Y master reproduce
    if (active && master.playing) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [master.playing, master.speed, active, url, master.videoRef, master.seekTick]);

  // Corrección de drift periódica mientras está activo
  useEffect(() => {
    if (!active) return;
    const v = ref.current, m = master.videoRef.current;
    if (!v || !m) return;
    const id = setInterval(() => {
      if (!m.paused && !v.paused && Math.abs(v.currentTime - m.currentTime) > 0.25) {
        try { v.currentTime = m.currentTime; } catch (_) { /* noop */ }
      }
    }, 500);
    return () => clearInterval(id);
  }, [active, master.videoRef]);

  return (
    <video ref={ref} src={resolveVideoUrl(url)} muted playsInline preload="metadata"
           className="h-full w-full object-cover" data-drive-slave="1" data-testid={testid} />
  );
}

/* ---------- Panel wrapper (fijo o intercambiable) ---------- */
function Panel({ title, icon: Icon, testid, active, onSwap, swappable, aspect = "aspect-video", children }) {
  const clickable = swappable && !active;
  return (
    <motion.div layout onClick={clickable ? onSwap : undefined} data-testid={testid}
                whileHover={clickable ? { scale: 1.02 } : undefined}
                className={`relative ${aspect} rounded-xl overflow-hidden border transition-shadow ${active ? "border-orange-400 shadow-lg shadow-orange-500/30" : "border-white/10"} ${clickable ? "cursor-pointer hover:border-white/20" : ""}`}>
      {children}
      <div className="absolute top-2 left-2 flex items-center gap-1 text-[9px] uppercase tracking-widest text-white/80 bg-black/40 backdrop-blur-md px-1.5 py-0.5 rounded-full border border-white/10 z-10">
        <Icon size={10} /> {title}
        {!swappable && <span className="ml-1 text-white/40 normal-case">· fijo</span>}
      </div>
    </motion.div>
  );
}

/* ---------- Player principal ---------- */
export default function DrivePlayer() {
  const { lessonId } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [mainPanel, setMainPanel] = useState("video"); // video | interior | pedals | gear
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [seekTick, setSeekTick] = useState(0); // pulso para que los slaves resincronicen tras seek
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [preloading, setPreloading] = useState(false);
  const [preloadReady, setPreloadReady] = useState(0);
  const [preloadTotal, setPreloadTotal] = useState(0);
  const videoRef = useRef(null);
  const progressPushed = useRef(0);

  useEffect(() => {
    api.get(`/drive/lessons/${lessonId}`).then((r) => {
      setLesson(r.data);
      setNotes(r.data.my_notes || "");
      const restore = r.data.progress_sec;
      if (restore && videoRef.current) {
        const v = videoRef.current;
        const applySeek = () => {
          if (isFinite(v.duration) && restore > 0 && restore < v.duration - 1) {
            v.currentTime = restore;
            setSeekTick((t) => t + 1);
          }
          v.removeEventListener("loadedmetadata", applySeek);
        };
        if (v.readyState >= 1) applySeek();
        else v.addEventListener("loadedmetadata", applySeek);
      }
    }).catch((e) => { toast.error(errMsg(e)); nav("/app/academia/drive"); });
  }, [lessonId, nav]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) v.play().then(() => setPlaying(true)).catch((err) => toast.error(`Reproducción bloqueada: ${err.name} — ${err.message}`));
    else { v.pause(); setPlaying(false); }
  }, []);
  const toggleMute = useCallback(() => {
    const v = videoRef.current; if (!v) return; v.muted = !v.muted; setMuted(v.muted);
  }, []);
  const seek = useCallback((t) => {
    const v = videoRef.current; if (!v) return;
    v.currentTime = t;
    setProgress(t);
    setSeekTick((x) => x + 1);
  }, []);
  const fullscreen = useCallback(() => {
    const el = videoRef.current?.parentElement; if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  }, []);

  useEffect(() => { const v = videoRef.current; if (v) v.playbackRate = speed; }, [speed]);

  const preloadAllAndPlay = useCallback(async () => {
    if (preloading) return;
    const master = videoRef.current; if (!master) return;
    setPreloading(true);
    // Reúne master + slaves ya montados en el DOM (Profesor y Velocidad siempre; los
    // intercambiables mientras estén en la vista principal o miniatura).
    const slaves = Array.from(document.querySelectorAll('video[data-drive-slave="1"]'));
    const all = [master, ...slaves];
    setPreloadTotal(all.length);
    setPreloadReady(0);
    // Fuerza precarga completa
    all.forEach((v) => { try { v.preload = "auto"; v.load(); } catch (_) { /* noop */ } });
    // Espera a canplaythrough (o timeout 15 s) en cada uno
    const waitReady = (v) => new Promise((resolve) => {
      if (v.readyState >= 3) return resolve();
      const to = setTimeout(finish, 15000);
      function finish() {
        clearTimeout(to);
        v.removeEventListener("canplaythrough", finish);
        v.removeEventListener("canplay", finish);
        setPreloadReady((n) => n + 1);
        resolve();
      }
      v.addEventListener("canplaythrough", finish);
      v.addEventListener("canplay", finish);
    });
    await Promise.all(all.map(waitReady));
    // Alinea slaves al currentTime del master antes de arrancar
    slaves.forEach((v) => { try { v.currentTime = master.currentTime; } catch (_) { /* noop */ } });
    // Reproduce master (los slaves activos se disparan solos por el efecto de sync)
    try {
      await master.play();
      setPlaying(true);
      toast.success("Cockpit sincronizado · reproducción arrancada");
    } catch (err) {
      toast.error(`Reproducción bloqueada: ${err.name} — ${err.message}`);
    }
    setPreloading(false);
  }, [preloading]);

  useEffect(() => {
    const v = videoRef.current; if (!v) return;
    const onTime = () => {
      setProgress(v.currentTime);
      if (Math.abs(v.currentTime - progressPushed.current) > 8) {
        progressPushed.current = v.currentTime;
        api.post(`/drive/lessons/${lessonId}/progress`, { position_sec: v.currentTime }).catch(() => {});
      }
    };
    const onEnded = () => {
      api.post(`/drive/lessons/${lessonId}/progress`, { position_sec: v.duration, completed: true }).catch(() => {});
      setPlaying(false);
      toast.success("Lección completada · progreso guardado");
    };
    const onSeeked = () => setSeekTick((t) => t + 1);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("ended", onEnded);
    v.addEventListener("seeked", onSeeked);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("seeked", onSeeked);
    };
  }, [lessonId, lesson]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try { await api.put(`/drive/lessons/${lessonId}/notes`, { notes }); toast.success("Notas guardadas"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSavingNotes(false); }
  };

  const master = useMemo(() => ({ videoRef, playing, speed, seekTick }), [playing, speed, seekTick]);

  if (!lesson) return <div className="min-h-screen bg-black flex items-center justify-center text-white/50">Cargando…</div>;

  // Vídeos secundarios: URL o null (usa fallback decorativo)
  const proUrl = lesson.professor_video_url || lesson.instructor_video_url || "";
  const intUrl = lesson.interior_video_url || "";
  const pedUrl = lesson.pedals_video_url || "";
  const gearUrl = lesson.gear_video_url || "";
  const speedUrl = lesson.speed_video_url || "";
  const total = lesson.duration_sec || 1;
  const multiTrackCount = [proUrl, intUrl, pedUrl, gearUrl, speedUrl].filter(Boolean).length;
  const hasMultiTrack = multiTrackCount > 0;

  // El panel activo en la vista principal (además del master original)
  const mainIsVideo = mainPanel === "video";

  return (
    <div className="min-h-[calc(100vh-3rem)] bg-[#050608] text-white" data-testid="drive-player">
      <div className="flex items-center gap-3 px-6 md:px-10 pt-6 pb-4">
        <button onClick={() => nav("/app/academia/drive")} className="text-white/70 hover:text-white flex items-center gap-1.5" data-testid="drive-back-btn">
          <ArrowLeft size={17} /> <span className="text-sm">Biblioteca</span>
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-orange-400 font-semibold">Natur Drive 360</p>
          <h1 className="font-heading text-xl md:text-2xl truncate">{lesson.title}</h1>
        </div>
        <Button variant="ghost" onClick={() => setShowNotes((s) => !s)} data-testid="drive-notes-toggle"
                className="text-white/70 hover:text-orange-400 gap-1.5">
          <StickyNote size={16} /> Mis notas
        </Button>
      </div>

      <div className="px-4 md:px-10 pb-10 grid gap-4" style={{ gridTemplateColumns: "minmax(0,1fr) 340px" }}>
        {/* Columna principal: master (persistente) + panel intercambiable */}
        <div className="space-y-4">
          <div className="aspect-video max-h-[72vh] relative">
            <div className={mainIsVideo ? "h-full w-full" : "absolute inset-0 opacity-0 pointer-events-none"}>
              <div className="relative h-full w-full bg-black rounded-2xl overflow-hidden border border-white/10 group">
                <video ref={videoRef} src={resolveVideoUrl(lesson.video_url)}
                       poster={lesson.thumbnail_url || undefined}
                       className="h-full w-full object-cover" playsInline preload="metadata"
                       data-testid="drive-main-video" />
                <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
                  <span className="text-[10px] uppercase tracking-widest bg-white/10 backdrop-blur-md px-2 py-1 rounded-full border border-white/10 text-white/80">{lesson.category}</span>
                </div>
                {!playing && (
                  <button data-testid="drive-play-overlay" onClick={hasMultiTrack ? preloadAllAndPlay : togglePlay}
                          disabled={preloading}
                          className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 hover:bg-black/50 z-10 gap-3">
                    <div className="h-20 w-20 rounded-full bg-white flex items-center justify-center shadow-2xl">
                      <Play size={28} className="text-black ml-1" fill="currentColor" />
                    </div>
                    {hasMultiTrack && (
                      <div data-testid="drive-play-all-badge"
                           className="px-4 py-1.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-black text-xs font-bold uppercase tracking-widest shadow-lg">
                        {preloading
                          ? `Precargando ${preloadReady}/${preloadTotal} pistas…`
                          : `▶ Reproducir todas · ${multiTrackCount + 1} cámaras`}
                      </div>
                    )}
                  </button>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-3 pt-8 bg-gradient-to-t from-black/95 via-black/70 to-transparent z-10">
                  <div className="relative h-1.5 bg-white/10 rounded-full cursor-pointer" data-testid="drive-timeline"
                       onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seek(((e.clientX - r.left) / r.width) * total); }}>
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-500 to-amber-400 rounded-full" style={{ width: `${(progress / total) * 100}%` }} />
                    {(lesson.chapters || []).map((c, i) => (
                      <div key={i} title={c.label}
                           className={`absolute top-1/2 -translate-y-1/2 h-3 w-1 rounded-full ${CHAP_KIND_COLOR[c.kind] || "bg-white"} cursor-pointer hover:h-4`}
                           style={{ left: `${(c.ts / total) * 100}%` }}
                           onClick={(ev) => { ev.stopPropagation(); seek(c.ts); }} />
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-white">
                    <button onClick={togglePlay} className="hover:text-orange-400" data-testid="drive-play-btn">
                      {playing ? <Pause size={18} /> : <Play size={18} fill="currentColor" />}
                    </button>
                    <button onClick={() => seek(Math.max(0, progress - 10))} className="hover:text-orange-400"><ChevronsLeft size={18} /></button>
                    <button onClick={() => seek(Math.min(total, progress + 10))} className="hover:text-orange-400"><ChevronsRight size={18} /></button>
                    <button onClick={toggleMute} className="hover:text-orange-400">
                      {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                    </button>
                    <span className="text-xs text-white/60 tabular-nums">{fmt(progress)} / {fmt(total)}</span>
                    <select value={speed} onChange={(e) => setSpeed(parseFloat(e.target.value))}
                            className="text-[11px] bg-white/10 border border-white/10 rounded-full px-2 py-0.5 text-white cursor-pointer"
                            data-testid="drive-speed-select">
                      {[0.5, 0.75, 1, 1.25, 1.5, 2].map((s) => <option key={s} value={s} className="text-black">{s}×</option>)}
                    </select>
                    <div className="flex-1" />
                    <button onClick={fullscreen} className="hover:text-orange-400"><Maximize size={17} /></button>
                  </div>
                </div>
              </div>
            </div>
            {!mainIsVideo && (
              <div className="h-full w-full rounded-2xl overflow-hidden border border-orange-400/40 shadow-xl shadow-orange-500/20 relative">
                {mainPanel === "interior" && (intUrl
                  ? <SlaveVideo url={intUrl} master={master} active testid="drive-slave-interior-main" />
                  : <InteriorDeco />)}
                {mainPanel === "pedals" && (pedUrl
                  ? <SlaveVideo url={pedUrl} master={master} active testid="drive-slave-pedals-main" />
                  : <PedalsDeco progress={progress} />)}
                {mainPanel === "gear" && (gearUrl
                  ? <SlaveVideo url={gearUrl} master={master} active testid="drive-slave-gear-main" />
                  : <GearboxDeco progress={progress} />)}
                <button onClick={() => setMainPanel("video")} data-testid="drive-back-to-main"
                        className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-white/10 backdrop-blur-md px-2 py-1 rounded-full border border-white/10 text-white/80 hover:bg-white/20 z-10">
                  ← Volver al vídeo
                </button>
              </div>
            )}
          </div>

          {/* Capítulos + notas del profesor */}
          <div className="bg-white/[0.03] rounded-2xl border border-white/10 p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-sm uppercase tracking-widest text-white/80">Capítulos</h3>
              <span className="text-[10px] text-white/40">{lesson.chapters?.length || 0} marcadores</span>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {(lesson.chapters || []).map((c, i) => (
                <button key={i} onClick={() => seek(c.ts)} data-testid="drive-chapter-item"
                        className={`text-left px-3 py-2 rounded-lg border ${progress >= c.ts ? "bg-white/5 border-orange-400/30" : "bg-transparent border-white/5 hover:bg-white/5"}`}>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-2 w-2 rounded-full ${CHAP_KIND_COLOR[c.kind] || "bg-white/60"}`} />
                    <span className="text-[11px] tabular-nums text-white/50 w-10">{fmt(c.ts)}</span>
                    <span className="text-sm text-white/90 truncate">{c.label}</span>
                  </div>
                </button>
              ))}
              {(!lesson.chapters || lesson.chapters.length === 0) && (
                <p className="col-span-2 text-xs text-white/40">Esta lección aún no tiene marcadores.</p>
              )}
            </div>
            {lesson.teacher_notes && (
              <div className="mt-4 text-xs text-white/70 bg-orange-500/5 border border-orange-500/20 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-widest text-orange-300 mb-1">Notas del profesor</p>
                {lesson.teacher_notes}
              </div>
            )}
          </div>
        </div>

        {/* Columna cockpit: alturas heterogéneas para no parecer una lista uniforme */}
        <div className="space-y-3">
          {/* 1. PROFESOR — fijo, siempre activo (16:9) */}
          <Panel title="Profesor" icon={User} testid="drive-panel-professor" active={false} swappable={false}>
            {proUrl ? <SlaveVideo url={proUrl} master={master} active={true /* fijo: siempre acompaña al master */} testid="drive-slave-professor" />
                    : <ProfessorDeco />}
          </Panel>

          {/* 2. VISTA INTERIOR — intercambiable (16:9) */}
          {mainPanel !== "interior" && (
            <Panel title="Vista interior" icon={Car} testid="drive-panel-interior" active={false} swappable
                   onSwap={() => setMainPanel("interior")}>
              {intUrl ? <SlaveVideo url={intUrl} master={master} active={false} testid="drive-slave-interior-thumb" />
                      : <InteriorDeco />}
            </Panel>
          )}

          {/* 3. PEDALES — intercambiable (16:9) */}
          {mainPanel !== "pedals" && (
            <Panel title="Pedales" icon={Radio} testid="drive-panel-pedals" active={false} swappable
                   onSwap={() => setMainPanel("pedals")}>
              {pedUrl ? <SlaveVideo url={pedUrl} master={master} active={false} testid="drive-slave-pedals-thumb" />
                      : <PedalsDeco progress={progress} />}
            </Panel>
          )}

          {/* 4. MARCHAS — intercambiable, compacto panorámico */}
          {mainPanel !== "gear" && (
            <Panel title="Marchas" icon={Radio} testid="drive-panel-gear" active={false} swappable
                   onSwap={() => setMainPanel("gear")} aspect="aspect-[16/6]">
              {gearUrl ? <SlaveVideo url={gearUrl} master={master} active={false} testid="drive-slave-gear-thumb" />
                       : <GearboxDeco progress={progress} />}
            </Panel>
          )}

          {/* 5. VELOCIDAD — fijo, siempre visible como instrumento de apoyo, panorámico */}
          <Panel title="Velocidad" icon={Gauge} testid="drive-panel-speed" active={false} swappable={false}
                 aspect="aspect-[16/5]">
            {speedUrl ? <SlaveVideo url={speedUrl} master={master} active={true} testid="drive-slave-speed" />
                      : <DashboardDeco progress={progress} />}
          </Panel>

          {/* 6. INSTA360 — fijo, sólo acceso externo (no sync, no swap) */}
          {lesson.insta360_url && (
            <a href={lesson.insta360_url} target="_blank" rel="noreferrer"
               className="group block relative aspect-video rounded-xl overflow-hidden border border-cyan-400/40 hover:border-cyan-400 transition-shadow shadow-lg shadow-cyan-500/10"
               data-testid="drive-panel-insta360">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-black">
                {lesson.insta360_thumbnail_url && (
                  <img src={lesson.insta360_thumbnail_url} alt="" className="h-full w-full object-cover opacity-70 group-hover:opacity-90 transition" />
                )}
              </div>
              <div className="absolute top-2 left-2 flex items-center gap-1 text-[9px] uppercase tracking-widest text-cyan-100 bg-cyan-400/20 backdrop-blur-md px-1.5 py-0.5 rounded-full border border-cyan-400/40 z-10">
                <Compass size={10} /> Experiencia 360°
              </div>
              <div className="absolute top-2 right-2 z-10 text-[8px] uppercase tracking-widest text-cyan-100 bg-cyan-400/30 px-1.5 py-0.5 rounded-full">Insta360</div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-12 w-12 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
                  <Play size={18} className="text-cyan-600 ml-0.5" fill="currentColor" />
                </div>
              </div>
              <div className="absolute bottom-2 left-2 right-2 text-[10px] text-white/80">Abrir experiencia 360° publicada →</div>
            </a>
          )}

          <div className="text-[10px] text-white/30 text-center pt-1">
            Todo se sincroniza por <code>currentTime</code> del vídeo principal · pausa al pausar · velocidad heredada
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showNotes && (
          <motion.div initial={{ x: 400, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 400, opacity: 0 }}
                       className="fixed top-0 right-0 h-full w-full max-w-md bg-neutral-950 border-l border-white/10 shadow-2xl z-40 p-6 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading">Mis notas</h3>
              <button onClick={() => setShowNotes(false)} className="text-white/60 hover:text-white">×</button>
            </div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="drive-notes-textarea"
                      placeholder="Anota tus observaciones, dudas o recordatorios sobre esta lección…"
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-white placeholder:text-white/30 resize-none" />
            <Button onClick={saveNotes} disabled={savingNotes} data-testid="drive-notes-save"
                    className="mt-3 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-semibold">
              {savingNotes ? "Guardando…" : "Guardar notas"}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
